import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import React, { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder/options'
import ProjectBlock from './ProjectBlock'
import type { ZoomLevel } from './TimelineHeader'

const useStyles = makeStyles<Theme, CrewSubRowProps>(
  (theme) => ({
    root: {
      display: 'flex',
      transition: 'all 0.2s',
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      overflow: 'hidden'
    },
    draggingRoot: {
      opacity: 0.5
    },
    isOverCrew: {
      border: `2px solid ${theme.palette.primary.main}`
    },
    overCell: {
      backgroundColor: 'rgba(191, 219, 254, 0.3)' // bg-blue-50/30
    },
    notOverCell: {
      '&:hover': {
        backgroundColor: 'rgba(241, 245, 249, 0.2)' // hover:bg-slate-50/20
      }
    },
    cell: {
      flexShrink: 0,
      boxSizing: 'border-box',
      backgroundColor: '#fff',
      borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      '&:first-child': {
        borderLeft: 'none'
      }
    },
    weekendDayCell: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
      zIndex: 1
    },
    timelineSection: {
      flex: 1,
      position: 'relative',
      display: 'flex',
      overflow: 'visible'
    },
    todayHighlight: {
      position: 'absolute',
      backgroundColor: 'rgba(227, 241, 251, 0.7)', // semanticPalette.Info.S95 (#e3f1fb) at 0.7 alpha
      pointerEvents: 'none'
    },
    columnHoverHighlight: {
      position: 'absolute',
      backgroundColor: 'rgba(191, 219, 254, 0.3)', // bg-blue-100/30
      pointerEvents: 'none'
    },
    dropZoneHighlight: {
      position: 'absolute',
      backgroundColor: '#bfdbfe', // Tailwind bg-blue-200
      borderRadius: 4,
      pointerEvents: 'none'
    },
    projectBlocksContainer: {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 50
    },
    projectBlockContainer: {
      position: 'absolute',
      pointerEvents: 'auto',
      overflow: 'visible'
    }
  }),
  { name: 'CrewSubRow' }
)

export interface CrewSubRowProps {
  classes?: StyleClasses<typeof useStyles>
  crewId: string
  events?: CalendarEvent[]
  startDate: Date
  endDate: Date
  onProjectHover: (projectId: string | null) => void
  hoveredProject: string | null
  hoveredDate?: string | null
  onDrop: (eventId: string, crewId: string, newStartDate: Date) => void
  zoomLevel: ZoomLevel
  cellWidth: number
  onEventUpdate: (event: CalendarEvent) => void
  parentProjectManagerId: string
  onMoveCrew?: (draggedCrewId: string, targetCrewId: string) => void
}

function CrewSubRow(props: CrewSubRowProps) {
  const classes = useStyles(props)
  const {
    crewId,
    events,
    startDate,
    endDate,
    onProjectHover,
    hoveredProject,
    hoveredDate,
    onDrop,
    zoomLevel,
    cellWidth,
    onEventUpdate,
    parentProjectManagerId,
    onMoveCrew
  } = props
  const { options } = useClientOptions()
  const crews = getCrews({ options })
  const crew = crews.find((c) => c._id === crewId)
  const crewColor = crew?.color
  // const crew = crews.find((c) => c.id === crewId)
  const [_localHoveredDate, setLocalHoveredDate] = useState<string | null>(null)
  const [draggedDuration, setDraggedDuration] = useState<number>(0)
  const ref = useRef<HTMLDivElement>(null)

  const [{ isDragging }] = useDrag({
    type: 'CREW',
    item: { crewId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOverCrew }, dropCrew] = useDrop({
    accept: 'CREW',
    drop: (item: { crewId: string }) => {
      if (item.crewId !== crewId && onMoveCrew) {
        onMoveCrew(item.crewId, crewId)
      }
    },
    collect: (monitor) => ({
      isOverCrew: monitor.isOver()
    })
  })

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'event',
      drop: (item: { eventId: string; duration?: number }, monitor) => {
        const offset = monitor.getClientOffset()
        if (!offset) return

        // Use elementsFromPoint (plural) to look through all layers — the custom
        // drag preview sits at zIndex 100 directly over the cursor and would
        // block elementFromPoint (singular) from reaching the [data-date] cell.
        // Shift one cell left via previousElementSibling to match the visual snap.
        let dayCell: Element | null = null
        for (const el of document.elementsFromPoint(offset.x, offset.y)) {
          const cell = el.closest('[data-date]')
          if (cell) {
            dayCell = cell
            break
          }
        }
        let handled = false
        if (dayCell) {
          const dateStr = dayCell.getAttribute('data-date')
          if (dateStr) {
            const newStartDate = new Date(dateStr)
            onDrop(item.eventId, crewId, newStartDate)
            handled = true
          }
        }
        setLocalHoveredDate(null)
        setDraggedDuration(0)
        // Signal to outer fallback drop on EventsAgendaView's timeline body
        // that this row already handled the drop, so it skips via didDrop().
        if (handled) return { handled: true }
      },
      hover: (item: { eventId: string; duration?: number }, monitor) => {
        const offset = monitor.getClientOffset()
        if (!offset) {
          setLocalHoveredDate(null)
          setDraggedDuration(0)
          return
        }

        let dayCell: Element | null = null
        for (const el of document.elementsFromPoint(offset.x, offset.y)) {
          const cell = el.closest('[data-date]')
          if (cell) {
            dayCell = cell
            break
          }
        }
        if (dayCell) {
          const dateStr = dayCell.getAttribute('data-date')
          setLocalHoveredDate(dateStr)
          setDraggedDuration(item.duration || 5)
        } else {
          setLocalHoveredDate(null)
          setDraggedDuration(0)
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    [parentProjectManagerId, onDrop, crewId]
  )

  useEffect(() => {
    if (!isOver) {
      setLocalHoveredDate(null)
      setDraggedDuration(0)
    }
  }, [isOver])

  if (!crew) return null

  // Sort events by start date to ensure earlier events appear first
  const sortedEvents = [...events].sort((a, b) => {
    if (!a.start) return 1
    if (!b.start) return -1
    return new Date(a.start).getTime() - new Date(b.start).getTime()
  })

  // Assign each event to a row using a smart row assignment algorithm
  // This ensures chronologically earlier events get earlier rows
  const eventRows = new Map<string, number>()
  const rowOccupancy: Array<Array<{ start: Date; end: Date }>> = []

  sortedEvents.forEach((event) => {
    if (!event.start || !event.end) return

    const eventStart = new Date(event.start)
    eventStart.setHours(0, 0, 0, 0)
    const eventEnd = new Date(event.end)
    eventEnd.setHours(23, 59, 59, 999) // Set to end of day for proper overlap detection

    // Find the first row where this event doesn't overlap with any existing event
    let assignedRow = -1
    for (let rowIndex = 0; rowIndex < rowOccupancy.length; rowIndex++) {
      const eventsInRow = rowOccupancy[rowIndex]
      let hasOverlap = false

      for (const occupiedEvent of eventsInRow) {
        const { start, end } = occupiedEvent
        // Check if date ranges overlap
        // Events overlap if: eventStart <= existingEnd AND eventEnd >= existingStart
        if (eventStart <= end && eventEnd >= start) {
          hasOverlap = true
          break
        }
      }

      if (!hasOverlap) {
        assignedRow = rowIndex
        break
      }
    }

    // If no suitable row was found, create a new one
    if (assignedRow === -1) {
      assignedRow = rowOccupancy.length
    }

    // Ensure the row exists in our occupancy tracker
    if (!rowOccupancy[assignedRow]) {
      rowOccupancy[assignedRow] = []
    }

    // Add this project to the row (store actual start and end dates)
    rowOccupancy[assignedRow].push({ start: eventStart, end: eventEnd })
    eventRows.set(event.id, assignedRow)
  })

  const maxRows = rowOccupancy.length || 1

  // Calculate minimum height based on number of rows
  // IMPORTANT: This must match calculateCrewHeight in EventsAgendaView.tsx exactly!
  // 12px top padding + (rows * 78px) + 0px bottom padding to match top padding
  // Job blocks are 66px, so with 78px row spacing, we get 12px above each job naturally
  const minHeight = Math.max(78, 12 + maxRows * 78)

  // Generate cells to match TimelineHeader exactly based on zoom level
  const cells = []
  const currentDate = new Date(startDate)
  let cellCount = 0

  // Helper to create a consistent date string in local timezone
  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Helper to check if a date is a weekend
  const isWeekend = (date: Date) => {
    const day = date.getDay()
    return day === 0 || day === 6
  }

  // Calculate the time unit each cell represents based on zoom level
  const getTimeUnitInDays = () => {
    switch (zoomLevel) {
      case 'days':
        return 1
      case 'weeks':
        return 7
      case 'months':
        return 30 // Approximate
      case 'quarters':
        return 90 // Approximate
      default:
        return 1
    }
  }

  const timeUnitInDays = getTimeUnitInDays()

  if (zoomLevel === 'days') {
    while (currentDate <= endDate) {
      const dateStr = getLocalDateString(currentDate)
      const isWeekendDay = isWeekend(currentDate)
      cells.push(
        <div
          key={cellCount}
          data-date={dateStr}
          className={classNames({ [classes.cell]: true, [classes.weekendDayCell]: isWeekendDay })}
          // className={`flex-shrink-0 border-r border-white ${isWeekendDay ? 'bg-[#fafbfc]' : ''}`}
          style={{ width: `${cellWidth}px` }}
        />
      )
      currentDate.setDate(currentDate.getDate() + 1)
      cellCount++
    }
  } else if (zoomLevel === 'weeks') {
    while (currentDate <= endDate) {
      const dateStr = getLocalDateString(currentDate)
      cells.push(
        <div key={cellCount} data-date={dateStr} className={classes.cell} style={{ width: `${cellWidth}px` }} />
      )
      currentDate.setDate(currentDate.getDate() + 7)
      cellCount++
    }
  } else if (zoomLevel === 'months') {
    const monthCursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    const monthEnd = new Date(endDate.getFullYear(), endDate.getMonth(), 1)

    while (monthCursor <= monthEnd) {
      const dateStr = getLocalDateString(monthCursor)
      cells.push(
        <div key={cellCount} data-date={dateStr} className={classes.cell} style={{ width: `${cellWidth}px` }} />
      )
      monthCursor.setMonth(monthCursor.getMonth() + 1, 1)
      cellCount++
    }
  } else if (zoomLevel === 'quarters') {
    const quarterStartMonth = Math.floor(startDate.getMonth() / 3) * 3
    const quarterEndMonth = Math.floor(endDate.getMonth() / 3) * 3
    const quarterCursor = new Date(startDate.getFullYear(), quarterStartMonth, 1)
    const quarterEnd = new Date(endDate.getFullYear(), quarterEndMonth, 1)

    while (quarterCursor <= quarterEnd) {
      const dateStr = getLocalDateString(quarterCursor)
      cells.push(
        <div key={cellCount} data-date={dateStr} className={classes.cell} style={{ width: `${cellWidth}px` }} />
      )
      quarterCursor.setMonth(quarterCursor.getMonth() + 3, 1)
      cellCount++
    }
  }

  // const isOverCell = Boolean(localHoveredDate)

  return (
    // <Droppable droppableId={`crew-${crew.id}`} direction="horizontal">
    //   {(droppableProvided, _snapshot) => {
    //     return (
    <div
      ref={(node) => {
        ref.current = node
        drop(node)
        dropCrew(node)
      }}
      data-crew-id={crewId}
      className={classNames({
        [classes.root]: true,
        // [isOver ? classes.overCell : classes.notOverCell]: true,
        [classes.draggingRoot]: isDragging,
        [classes.isOverCrew]: isOverCrew
      })}
      style={{ height: `${minHeight}px` }}
    >
      {/* Timeline Section */}
      <div className={classes.timelineSection}>
        {cells}

        {/* Today Column Highlight */}
        {(() => {
          const today = new Date(Date.now())
          today.setHours(0, 0, 0, 0)

          // Normalize startDate and endDate to avoid timezone issues
          const normalizedStart = new Date(startDate)
          normalizedStart.setHours(0, 0, 0, 0)
          const normalizedEnd = new Date(endDate)
          normalizedEnd.setHours(23, 59, 59, 999)

          let showHighlight = false
          let leftPosition = 0
          let width = cellWidth

          if (today >= normalizedStart && today <= normalizedEnd) {
            switch (zoomLevel) {
              case 'days': {
                const daysDiff = Math.round((today.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24))
                leftPosition = daysDiff * cellWidth
                width = cellWidth
                showHighlight = true
                break
              }
              case 'weeks': {
                // Find the start of the week for today and for the timeline
                const getWeekStart = (date: Date) => {
                  const d = new Date(date)
                  d.setHours(0, 0, 0, 0)
                  d.setDate(d.getDate() - d.getDay())
                  return d
                }
                const weekStart = getWeekStart(today)
                const timelineWeekStart = getWeekStart(normalizedStart)
                const weeksDiff = Math.round(
                  (weekStart.getTime() - timelineWeekStart.getTime()) / (1000 * 60 * 60 * 24 * 7)
                )
                leftPosition = weeksDiff * cellWidth
                width = cellWidth
                // Only show if weekStart is in range
                if (weekStart >= timelineWeekStart && weekStart <= normalizedEnd) {
                  showHighlight = true
                }
                break
              }
              case 'months': {
                const monthsDiff =
                  (today.getFullYear() - normalizedStart.getFullYear()) * 12 +
                  (today.getMonth() - normalizedStart.getMonth())
                leftPosition = monthsDiff * cellWidth
                width = cellWidth
                // Only show if month is in range
                const startMonth = new Date(normalizedStart.getFullYear(), normalizedStart.getMonth(), 1)
                const endMonth = new Date(normalizedEnd.getFullYear(), normalizedEnd.getMonth(), 1)
                const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1)
                if (todayMonth >= startMonth && todayMonth <= endMonth) {
                  showHighlight = true
                }
                break
              }
              case 'quarters': {
                const getQuarter = (date: Date) => Math.floor(date.getMonth() / 3)
                const startQuarter = getQuarter(normalizedStart)
                const todayQuarter = getQuarter(today)
                const quartersDiff =
                  (today.getFullYear() - normalizedStart.getFullYear()) * 4 + (todayQuarter - startQuarter)
                leftPosition = quartersDiff * cellWidth
                width = cellWidth
                // Only show if quarter is in range
                const startQDate = new Date(normalizedStart.getFullYear(), startQuarter * 3, 1)
                const endQDate = new Date(normalizedEnd.getFullYear(), getQuarter(normalizedEnd) * 3, 1)
                const todayQDate = new Date(today.getFullYear(), todayQuarter * 3, 1)
                if (todayQDate >= startQDate && todayQDate <= endQDate) {
                  showHighlight = true
                }
                break
              }
              default:
                break
            }
          }

          if (showHighlight) {
            return (
              <div
                className={classes.todayHighlight}
                style={{
                  left: `${leftPosition}px`,
                  top: 0,
                  width: `${width}px`,
                  height: '100%'
                }}
              />
            )
          }
          return null
        })()}

        {/* Column Hover Highlight */}
        {hoveredDate &&
          !draggedDuration &&
          (() => {
            // Parse the hoveredDate string into local time components (not UTC!)
            const [year, month, day] = hoveredDate.split('-').map(Number)
            const hoveredDateObj = new Date(year, month - 1, day)
            hoveredDateObj.setHours(0, 0, 0, 0)

            // Normalize startDate to avoid timezone issues
            const normalizedStart = new Date(startDate)
            normalizedStart.setHours(0, 0, 0, 0)

            const daysFromStart = Math.round(
              (hoveredDateObj.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24)
            )
            const leftPosition = daysFromStart * cellWidth

            return (
              <div
                className={classes.columnHoverHighlight}
                style={{
                  left: `${leftPosition}px`,
                  top: 0,
                  width: `${cellWidth}px`,
                  height: '100%'
                }}
              />
            )
          })()}

        {/* Drop Zone Highlight */}
        {/* {localHoveredDate &&
          draggedDuration > 0 &&
          (() => {
            const hoveredDateObj = new Date(localHoveredDate)
            hoveredDateObj.setHours(0, 0, 0, 0)

            // Normalize startDate to avoid timezone issues
            const normalizedStart = new Date(startDate)
            normalizedStart.setHours(0, 0, 0, 0)

            const daysFromStart = Math.round(
              (hoveredDateObj.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24)
            )
            const leftPosition = daysFromStart * cellWidth
            const widthPixels = draggedDuration * cellWidth

            return (
              <div
                className={classes.dropZoneHighlight}
                style={{
                  left: `${leftPosition}px`,
                  top: '8px',
                  width: `${widthPixels}px`,
                  height: '32px'
                }}
              />
            )
          })()} */}

        {/* Event Blocks */}
        <div className={classes.projectBlocksContainer}>
          {sortedEvents.map((event) => {
            if (!event.start || !event.end) return null

            const eventStart = new Date(event.start)
            eventStart.setHours(0, 0, 0, 0)
            const eventEnd = new Date(event.end)
            eventEnd.setHours(0, 0, 0, 0)

            // Normalize startDate to avoid timezone issues
            const normalizedStart = new Date(startDate)
            normalizedStart.setHours(0, 0, 0, 0)

            // Calculate days from timeline start
            const daysFromStart = Math.round((eventStart.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24))

            // Calculate project duration in days (inclusive of both start and end dates)
            const projectDuration = Math.round((eventEnd.getTime() - eventStart.getTime()) / (1000 * 60 * 60 * 24)) + 1

            // Convert days to cells based on zoom level
            const cellsFromStart = daysFromStart / timeUnitInDays
            const cellsDuration = projectDuration / timeUnitInDays

            // Use pixel-based positioning for consistent sizing across date range changes
            const rawLeft = cellsFromStart * cellWidth
            const rawWidth = Math.max(12, cellsDuration * cellWidth - 8)
            // If the project starts before the visible window, clamp the block to
            // the timeline's left edge so its label/type stay visible instead of
            // being pushed off-screen behind the sticky left column.
            const offscreenLeft = Math.max(0, -rawLeft)
            const leftPosition = Math.max(0, rawLeft)
            const widthPixels = Math.max(12, rawWidth - offscreenLeft)
            const extendsBeforeView = offscreenLeft > 0
            // Get the row index for this project
            const rowIndex = eventRows.get(event.id) || 0
            return (
              <div
                key={event.id}
                className={classes.projectBlockContainer}
                style={{
                  left: `${leftPosition}px`,
                  top: `${12 + rowIndex * 78}px`,
                  width: `${widthPixels}px`,
                  zIndex: rowIndex
                }}
              >
                <ProjectBlock
                  event={event}
                  onHover={onProjectHover}
                  isHovered={hoveredProject === event.id}
                  color={crewColor}
                  onEventUpdate={onEventUpdate}
                  cellWidth={cellWidth}
                  zoomLevel={zoomLevel}
                  extendsBeforeView={extendsBeforeView}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CrewSubRow
